// Home Page - Landing page with featured products, markets, and social
import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import useSEO from '../hooks/useSEO';
import { isMarketUpcoming } from '../utils/marketUtils';
import ProductCard from '../components/ProductCard';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';

// Renders text as an SVG <text> whose stroke "writes itself" on letter by
// letter, then fills in - a genuine left-to-right pen-stroke animation
// rather than a plain fade-in. There's no font-to-path conversion available
// in this toolchain, but SVG <text>/<tspan> support stroke and
// stroke-dashoffset directly. Each character gets its own <tspan> with a
// staggered animation-delay - a single dashoffset animation on the whole
// string doesn't work for this because text is made of independent
// per-glyph subpaths, so all letters would "draw" in parallel instead of
// in sequence (confirmed by inspecting an early animation frame).
//
// fontSize is in SVG viewBox units - the viewBox scales with both fontSize
// and text length, so title/subtitle/tagline instances stay proportioned
// correctly regardless of string length. startDelay (seconds) lets multiple
// instances chain into one sequence instead of all animating at once.
function AnimatedSignature({ text, className, fontSize = 110, startDelay = 0, ...svgProps }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    document.fonts.load('700 1em "Dancing Script"')
      .catch(() => {})
      .then(() => document.fonts.ready)
      .then(() => {
        if (!cancelled) setReady(true);
      });

    return () => { cancelled = true; };
  }, [text]);

  const chars = Array.from(text);
  const viewBoxWidth = Math.max(300, chars.length * fontSize * 0.62 + fontSize);
  const viewBoxHeight = fontSize * 1.9;

  return (
    <svg
      viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
      className={className}
      style={{ overflow: 'visible' }}
      {...svgProps}
    >
      <text
        x="50%"
        y="56%"
        textAnchor="middle"
        className="signature-text"
        style={{ fontSize: `${fontSize}px`, '--start-delay': `${startDelay}s` }}
      >
        {chars.map((char, index) => (
          <tspan
            key={index}
            className={`signature-char ${ready ? 'signature-char-ready' : ''}`}
            style={{ '--char-index': index }}
          >
            {char === ' ' ? ' ' : char}
          </tspan>
        ))}
      </text>
    </svg>
  );
}

export default function Home() {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [carouselImages, setCarouselImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [markets, setMarkets] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);

  // SEO metadata
  useSEO({
    title: 'Magnolia Mold Co - Handcrafted Polymer Clay Earrings',
    description: 'Unique, handcrafted polymer clay earrings made with love. Each piece is lightweight, comfortable, and one-of-a-kind. Shop our collection of beautiful handmade jewelry.',
    url: 'https://magnoliamoldco.com/',
    keywords: 'polymer clay earrings, handmade jewelry, lightweight earrings, unique earrings, custom jewelry, magnolia mold'
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    // Auto-rotate carousel every 4 seconds
    if (carouselImages.length > 0) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % carouselImages.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [carouselImages]);

  const loadData = async () => {
    try {
      // Load featured products
      const productsResponse = await fetch(`${API_URL}/api/public/products?inStock=true`);
      const productsData = await productsResponse.json();
      const shuffled = [...productsData.products].sort(() => 0.5 - Math.random());
      setFeaturedProducts(shuffled.slice(0, 8));

      // Get products with images for carousel
      const productsWithImages = productsData.products.filter(p => p.photoUrl);
      const randomImages = [...productsWithImages]
        .sort(() => 0.5 - Math.random())
        .slice(0, 10)
        .map(p => ({ url: p.photoUrl, name: p.description || p.name }));
      setCarouselImages(randomImages);

      // Load markets
      const marketsResponse = await fetch(`${API_URL}/api/content/markets`);
      const marketsData = await marketsResponse.json();
      setMarkets(marketsData);

      // Load stores
      const storesResponse = await fetch(`${API_URL}/api/content/stores`);
      const storesData = await storesResponse.json();
      setStores(storesData);

      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
    }
  };

  const upcomingMarkets = markets.filter((market) => isMarketUpcoming(market));

  return (
    <div className="bg-cream-100">
      {/* Hero Section with Image Carousel */}
      <section className="relative h-[600px] md:h-[720px] overflow-hidden bg-ink">
        {/* Image Carousel */}
        <div className="absolute inset-0">
          {carouselImages.map((image, index) => {
            const total = carouselImages.length;
            const distance = Math.min(
              Math.abs(index - currentImageIndex),
              total - Math.abs(index - currentImageIndex)
            );
            // Only mount the current slide plus its immediate neighbors -
            // all slides are stacked via absolute/opacity rather than being
            // offscreen, so browsers treat every <img> as "in viewport" and
            // load them all at once regardless of the loading="lazy" hint,
            // which was making transient QUIC connection failures worse.
            const shouldLoad = distance <= 1;

            return (
              <div
                key={index}
                className={`absolute inset-0 transition-opacity duration-1000 ${
                  index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                }`}
              >
                {shouldLoad && (
                  <img
                    src={image.url}
                    alt={image.name}
                    className="w-full h-full object-cover"
                    loading={index === currentImageIndex ? 'eager' : 'lazy'}
                    onError={(e) => {
                      const attempts = Number(e.target.dataset.retryCount || 0);
                      if (attempts < 2) {
                        e.target.dataset.retryCount = attempts + 1;
                        setTimeout(() => {
                          const separator = image.url.includes('?') ? '&' : '?';
                          e.target.src = `${image.url}${separator}retry=${Date.now()}`;
                        }, 800 * (attempts + 1));
                      }
                    }}
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-b from-ink/50 via-ink/40 to-ink/70"></div>
              </div>
            );
          })}
        </div>

        {/* Overlay Content */}
        <div className="relative h-full flex items-center justify-center">
          <div className="w-full max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="mb-6 text-cream-50 drop-shadow-lg">
              <span className="sr-only">Magnolia Mold Co. </span>
              <AnimatedSignature
                text="Magnolia Mold Co."
                aria-hidden="true"
                fontSize={110}
                className="w-full max-w-[1152px] md:max-w-[1344px] lg:max-w-[1536px] mx-auto h-auto block"
              />
              <span className="sr-only">Handmade with Love</span>
              <AnimatedSignature
                text="Handmade with Love"
                aria-hidden="true"
                fontSize={60}
                startDelay={1.9}
                className="w-full max-w-[650px] md:max-w-[760px] lg:max-w-[860px] mx-auto h-auto block mt-2 text-gold-200"
              />
            </h1>
            <div className="max-w-3xl mx-auto">
              <p className="mb-10 drop-shadow-md text-cream-100/90">
                <span className="sr-only">Despite the toddler fits</span>
                <AnimatedSignature
                  text="Despite the toddler fits"
                  aria-hidden="true"
                  fontSize={40}
                  startDelay={3.8}
                  className="w-full max-w-[480px] md:max-w-[560px] lg:max-w-[640px] mx-auto h-auto block"
                />
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/shop"
                  className="px-9 py-3.5 bg-cream-50 text-sage-800 rounded-full font-medium tracking-wide hover:bg-white transition-colors shadow-lg"
                >
                  Shop the Collection
                </Link>
                <Link
                  to="/about"
                  className="px-9 py-3.5 border border-cream-100/80 text-cream-50 rounded-full font-medium tracking-wide hover:bg-cream-50/10 transition-colors"
                >
                  Our Story
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Carousel Indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2 z-10">
          {carouselImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentImageIndex(index)}
              className={`h-1.5 rounded-full transition-all ${
                index === currentImageIndex
                  ? 'bg-cream-50 w-8'
                  : 'bg-cream-50/40 hover:bg-cream-50/70 w-3'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={() => setCurrentImageIndex((prev) => (prev - 1 + carouselImages.length) % carouselImages.length)}
          className="hidden sm:block absolute left-4 top-1/2 -translate-y-1/2 bg-cream-50/15 hover:bg-cream-50/30 text-cream-50 p-3 rounded-full backdrop-blur-sm transition-all z-10"
          aria-label="Previous image"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button
          onClick={() => setCurrentImageIndex((prev) => (prev + 1) % carouselImages.length)}
          className="hidden sm:block absolute right-4 top-1/2 -translate-y-1/2 bg-cream-50/15 hover:bg-cream-50/30 text-cream-50 p-3 rounded-full backdrop-blur-sm transition-all z-10"
          aria-label="Next image"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </section>

      {/* Values strip */}
      <section className="bg-cream-50 border-b border-cream-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          <FeatureCard icon={icons.hand} title="Handcrafted" description="Every pair made by hand with love" />
          <FeatureCard icon={icons.sparkle} title="One of a Kind" description="Unique designs you won't find anywhere else" />
          <FeatureCard icon={icons.feather} title="Lightweight" description="Light, comfortable polymer clay" />
          <FeatureCard icon={icons.gift} title="Gift Ready" description="Packaged to delight someone special" />
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="The Collection"
            title="Featured Earrings"
            subtitle="A few favorites from the studio. Each piece is unique and made with attention to detail."
          />

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-2 border-cream-300 border-t-sage-600" />
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-10 mb-14">
              {featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}

          <div className="text-center">
            <Link to="/shop" className="btn-brand-outline">
              View All Earrings
            </Link>
          </div>
        </div>
      </section>

      {/* Where to Find Us */}
      <section className="py-20 md:py-24 bg-cream-50 border-y border-cream-300">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Visit Us"
            title="Where to Find Us"
            subtitle="Shop online anytime, or come say hello at a market or local shop."
          />

          <div className="grid md:grid-cols-2 gap-6">
            {/* Online Shopping */}
            <div className="bg-white rounded-2xl p-8 md:p-10 border border-cream-300 flex flex-col">
              <IconBadge>{icons.bag}</IconBadge>
              <h3 className="font-serif text-3xl font-semibold text-ink mb-3">Shop Online</h3>
              <p className="text-ink-soft mb-8 leading-relaxed">
                Browse the full collection anytime, anywhere. New designs are added regularly.
              </p>
              <Link to="/shop" className="btn-brand self-start mt-auto">
                Browse the Collection
              </Link>
            </div>

            {/* Local Markets */}
            <div className="bg-white rounded-2xl p-8 md:p-10 border border-cream-300 flex flex-col">
              <IconBadge>{icons.tent}</IconBadge>
              <h3 className="font-serif text-3xl font-semibold text-ink mb-5">Upcoming Markets</h3>
              <div className="space-y-5 mb-8">
                {upcomingMarkets.length > 0 ? (
                  upcomingMarkets.slice(0, 3).map((market) => (
                    <MarketDate
                      key={market.id}
                      date={market.date}
                      event={market.event}
                      location={market.location}
                      time={market.time}
                    />
                  ))
                ) : (
                  <p className="text-ink-soft">No markets on the calendar right now. Check back soon!</p>
                )}
              </div>
              <Link to="/contact" className="btn-brand-outline self-start mt-auto">
                Get Market Updates
              </Link>
            </div>
          </div>

          {/* Retail Partners */}
          {stores.length > 0 && (
            <div className="mt-16">
              <h3 className="font-serif text-3xl font-semibold text-ink text-center mb-2">Find Us in Stores</h3>
              <p className="text-ink-soft text-center mb-8">
                Visit these local boutiques to see our earrings in person
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {stores.map((store) => (
                  <StoreCard
                    key={store.id}
                    name={store.name}
                    address={store.address}
                    phone={store.phone}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="py-20 md:py-24 bg-sage-800 text-cream-50 relative overflow-hidden">
        <img
          src="/logo.png"
          alt=""
          aria-hidden="true"
          className="absolute -right-24 -bottom-16 w-[420px] opacity-[0.07] pointer-events-none select-none"
        />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="eyebrow text-gold-300 mb-4">Newsletter</p>
          <h2 className="font-serif text-4xl md:text-5xl font-semibold mb-4">Stay in the Loop</h2>
          <p className="text-lg text-cream-200/90 mb-10">
            New designs, market dates, and the occasional exclusive offer.
          </p>
          <form className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto">
            <input
              type="email"
              placeholder="Your email address"
              className="flex-1 px-6 py-3.5 rounded-full bg-cream-50 text-ink placeholder-ink-muted focus:outline-none focus:ring-2 focus:ring-gold-300"
            />
            <button
              type="submit"
              className="px-8 py-3.5 bg-gold-500 text-white rounded-full font-medium tracking-wide hover:bg-gold-600 transition-colors"
            >
              Subscribe
            </button>
          </form>
          <p className="text-xs text-cream-300/70 mt-4">
            No spam, ever. Unsubscribe anytime.
          </p>
        </div>
      </section>
    </div>
  );
}

function SectionHeading({ eyebrow, title, subtitle }) {
  return (
    <div className="text-center mb-14">
      <p className="eyebrow mb-3">{eyebrow}</p>
      <h2 className="section-title mb-4">{title}</h2>
      <div className="flourish mb-5" aria-hidden="true">✦</div>
      {subtitle && <p className="text-lg text-ink-soft max-w-2xl mx-auto">{subtitle}</p>}
    </div>
  );
}

function formatMarketDate(date) {
  const parsed = new Date(`${date}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

function MarketDate({ date, event, location, time }) {
  return (
    <div className="border-l-2 border-gold-400 pl-4">
      <div className="font-semibold text-ink">{event}</div>
      {date && <div className="text-sm text-sage-700 font-medium mt-0.5">{formatMarketDate(date)}{time && ` · ${time}`}</div>}
      {location && <div className="text-sm text-ink-soft mt-0.5">{location}</div>}
    </div>
  );
}

function StoreCard({ name, address, phone }) {
  return (
    <div className="bg-white rounded-xl p-6 border border-cream-300">
      <h4 className="font-serif text-xl font-semibold text-ink mb-2">{name}</h4>
      <p className="text-sm text-ink-soft mb-1">{address}</p>
      {phone && <p className="text-sm text-sage-700">{phone}</p>}
    </div>
  );
}

function IconBadge({ children }) {
  return (
    <div className="w-12 h-12 rounded-full bg-sage-50 text-sage-700 flex items-center justify-center mb-6">
      {children}
    </div>
  );
}

function FeatureCard({ icon, title, description }) {
  return (
    <div className="flex flex-col items-center text-center">
      <div className="text-sage-600 mb-3">{icon}</div>
      <h3 className="font-serif text-xl font-semibold text-ink mb-1">{title}</h3>
      <p className="text-sm text-ink-soft">{description}</p>
    </div>
  );
}

// Simple line icons (heroicons outline style) so the page doesn't lean on emoji
const iconProps = { className: 'w-6 h-6', fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, viewBox: '0 0 24 24' };
const icons = {
  hand: (
    <svg {...iconProps}><path strokeLinecap="round" strokeLinejoin="round" d="M10.05 4.575a1.575 1.575 0 10-3.15 0v3m3.15-3v-1.5a1.575 1.575 0 013.15 0v1.5m-3.15 0l.075 5.925m3.075.75V4.575m0 0a1.575 1.575 0 013.15 0V15M6.9 7.575a1.575 1.575 0 10-3.15 0v8.175a6.75 6.75 0 006.75 6.75h2.018a5.25 5.25 0 003.712-1.538l1.732-1.732a5.25 5.25 0 001.538-3.712l.003-2.024a.668.668 0 01.198-.471 1.575 1.575 0 10-2.228-2.228 3.818 3.818 0 00-1.12 2.687M6.9 7.575V12m6.27 4.318A4.49 4.49 0 0116.35 15" /></svg>
  ),
  sparkle: (
    <svg {...iconProps}><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" /></svg>
  ),
  feather: (
    <svg {...iconProps}><path strokeLinecap="round" strokeLinejoin="round" d="M20.24 12.24a6 6 0 00-8.49-8.49L5 10.5V19h8.5l6.74-6.76zM16 8L2 22M17.5 15H9" /></svg>
  ),
  gift: (
    <svg {...iconProps}><path strokeLinecap="round" strokeLinejoin="round" d="M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" /></svg>
  ),
  bag: (
    <svg {...iconProps}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007z" /></svg>
  ),
  tent: (
    <svg {...iconProps}><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.016a3.001 3.001 0 003.75.614m-16.5 0a3.004 3.004 0 01-.621-4.72L4.318 3.44A1.5 1.5 0 015.378 3h13.243a1.5 1.5 0 011.06.44l1.19 1.189a3 3 0 01-.621 4.72m-13.5 8.65h3.75a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75H6.75a.75.75 0 00-.75.75v3.75c0 .415.336.75.75.75z" /></svg>
  ),
};
